/* sb-dc-tplprefit.js - do the template corrections BEFORE the board is drawn.
   2026-09-22 (v3 - also puts the SVG rotate() angle on text, see ROTATION).

   THE PROBLEM THIS SOLVES
   -----------------------
   sb-dc-tplmerge.js and sb-dc-tplshrink.js both work the same way: poll the
   document, notice something wrong, write it back with updateLayer /
   removeLayer. That means the customer is shown the wrong board first and the
   corrections land on top of it, one round at a time. Measured on the preview
   theme (2026-09-17):

     vinyl banner political-01   board stopped moving at  4.9s
     retractable vote info                               10.2s
     step & repeat graduation                            14.2s
     step & repeat wordmark      never - merge was still going at 25s
                                 (9 rounds, 216 layers removed, 108 merged)

   WHAT THIS DOES INSTEAD
   ----------------------
   The corrections are applied to the layer data, once per build, before the
   board is painted:

     0. rotate  vertical text gets its rotate() angle from the SVG  (v3)
     1. merge   stacked lines that were one text block in the artwork
     2. shrink  a font whose glyphs are wider than the box it was given

   The merge and shrink rules are copied from sb-dc-tplmerge.js v3 and
   sb-dc-tplshrink.js v5, and the rotation rule from sb-dc-tplrot.js v8, so the
   outcome is identical; only the timing changes.

   ROTATION (v3, 2026-09-22)
   Measured on the feather flag template "Business 02 Not Just Bricks", which
   has one 90-degree text layer:

     live         cover lifted at 3.0s, then in front of the customer:
                  rotation 0 -> 90 (3.5s) -> 0 (4s) -> 90 (12s) -> 0 (12.5s)
                  -> 90 (15.5s)
     work theme   the same flips under the cover, lifted at 16.6s
     tplboard     tries 30, fixes 8     tplrot  rounds 6, applied 6

   The cause is two of our own files taking turns. sb-dc-tplboard.js rebuilds
   the board whenever the layer boxes differ from the layout it last wrote.
   sb-dc-tplrot.js turns the vertical text by swapping its width and height
   around the centre - which changes that layer's box. So tplboard rebuilds
   (rotation back to 0), tplrot turns it again, tplboard rebuilds again... up to
   tplboard's limit of 8 rebuilds.

   Every one of those rebuilds goes through window.__dcSvgToLayers, which this
   file already wraps, and the SVG text is its first argument. So the angle is
   read from that SVG and applied here, to the array, before it is drawn. The
   board tplboard writes is then already turned: tplrot finds nothing pending,
   the boxes stop moving, and tplboard stops after one rebuild.

   Rotation runs FIRST, before merge and shrink. Both skip rotated text (as
   tplmerge and tplshrink do), so a vertical line can no longer be merged into
   its neighbours or shrunk as if it were horizontal.

   The loadTemplate path (sb-dc-tplfix.js' first pass) has no SVG in hand, so
   the template SVG is also fetched once at start, exactly as tplrot does, and
   the same angles are applied to that document.

   TWO ENTRY POINTS, both covered (measured 2026-09-17):
     1. window.__dcSvgToLayers - used by sb-dc-tplboard.js when it rebuilds the
        template onto the real board. Wrapped: the array is corrected before it
        is handed to setDoc.
     2. api.loadTemplate - the app's own loader, which sb-dc-tplfix.js calls on
        the first pass. The document it produced is corrected and written back
        in ONE setDoc.

   The polling files (tplmerge, tplshrink, tplrot) stay loaded and are NOT
   edited. They are the safety net for a board this file never saw.

   FONTS: glyph widths can only be measured once the webfont is loaded. If the
   measurement is unavailable when a build happens, the shrink step is skipped
   for that build and left to sb-dc-tplshrink.js. Merging and rotation need no
   measurement and always run.

   Only active with ?tpl= in the URL. Diagnostics: window.__dcTplPrefit() */
(function () {
  'use strict';

  var CX_TOL = 0.35, Y_TOL_FRAC = 0.10, MAX_LINES = 6;   // merge, from tplmerge v3
  var MARGIN = 0.98, FLOOR = 0.75, MIN_DROP = 0.01;      // shrink, from tplshrink v5

  var info = {
    builds: 0, loads: 0, docFixes: 0, merged: 0, removed: 0, shrunk: 0, floored: 0,
    rotated: 0, rotPairs: 0, fontsReady: false, skippedFont: 0, wrapped: false,
    ms: [], why: ''
  };
  window.__dcTplPrefit = function () { return info; };

  function q(name) {
    try { return new URLSearchParams(location.search).get(name) || ''; }
    catch (e) { return ''; }
  }
  var TPL = q('tpl');
  if (!TPL) { info.why = 'no tpl'; return; }

  /* ---- fonts ------------------------------------------------------------ */
  var FAMS = ['Archivo', 'Anton', 'Oswald', 'Montserrat', 'Roboto'];
  (function warm() {
    if (!document.fonts || !document.fonts.load) return;
    FAMS.forEach(function (f) {
      try {
        document.fonts.load('400 100px "' + f + '"');
        document.fonts.load('700 100px "' + f + '"');
      } catch (e) {}
    });
    try {
      document.fonts.ready.then(function () { info.fontsReady = true; });
    } catch (e) {}
  })();

  var ctx = null;
  function emWidth(l) {
    if (!ctx) ctx = document.createElement('canvas').getContext('2d');
    var fam = l.fontFamily || 'Archivo';
    var font = (l.italic ? 'italic ' : '') + (l.bold ? '700 ' : '400 ') + '100px "' + fam + '"';
    try {
      if (document.fonts && document.fonts.check && !document.fonts.check(font)) {
        info.skippedFont++;
        return 0;
      }
    } catch (e) {}
    ctx.font = font;
    var em = 0, lines = String(l.text == null ? '' : l.text).split(/\r?\n/);
    for (var i = 0; i < lines.length; i++) {
      var w = ctx.measureText(lines[i]).width / 100;
      if (w > em) em = w;
    }
    return em * (+l.stretchX || 1);
  }

  /* ---- rotation (same rule as sb-dc-tplrot.js v8) ------------------------ */
  function norm(s) { return String(s || '').replace(/\s+/g, ' ').trim().toUpperCase(); }

  function angleOf(el) {
    var node = el, hops = 0;
    while (node && hops < 4) {
      var t = node.getAttribute && node.getAttribute('transform');
      if (t) {
        var m = /rotate\(\s*(-?[\d.]+)/.exec(t);
        if (m) {
          var a = parseFloat(m[1]);
          if (isFinite(a) && Math.abs(a) > 0.5) return a;
        }
      }
      node = node.parentNode;
      hops++;
    }
    return 0;
  }

  var ROT = null;            // [{text, angle}] once known
  var rotFrom = '';          // which SVG the table came from

  function rotTable(svgText) {
    var out = [];
    try {
      var doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
      var texts = doc.getElementsByTagName('text');
      for (var i = 0; i < texts.length; i++) {
        var a = angleOf(texts[i]);
        if (!a) continue;
        var s = norm(texts[i].textContent);
        if (s) out.push({ text: s, angle: a });
      }
    } catch (e) {}
    return out;
  }

  function learn(svgText) {
    if (typeof svgText !== 'string' || svgText.length < 20) return;
    var key = svgText.length + ':' + svgText.slice(0, 64);
    if (key === rotFrom && ROT) return;
    ROT = rotTable(svgText);
    rotFrom = key;
    info.rotPairs = ROT.length;
  }

  function angleFor(l) {
    if (!ROT || !ROT.length || !l || l.type !== 'text') return 0;
    var s = norm(l.text || l.name);
    for (var i = 0; i < ROT.length; i++) if (ROT[i].text === s) return ROT[i].angle;
    return 0;
  }

  function rotateLayers(layers) {
    if (!ROT || !ROT.length) return layers;
    layers.forEach(function (l) {
      if (!l || l.type !== 'text' || +l.rotation) return;
      var a = angleFor(l);
      if (!a) return;
      l.rotation = a;
      if (Math.abs(Math.abs(a) - 90) < 1) {
        var cx0 = (+l.xIn || 0) + (+l.widthIn || 0) / 2;
        var cy0 = (+l.yIn || 0) + (+l.heightIn || 0) / 2;
        var w = (+l.heightIn || 0), h = (+l.widthIn || 0);
        l.widthIn = w; l.heightIn = h;
        l.xIn = cx0 - w / 2; l.yIn = cy0 - h / 2;
      }
      info.rotated++;
    });
    return layers;
  }

  /* The loadTemplate path has no SVG in hand: fetch it once, as tplrot does. */
  (function fetchSvgOnce() {
    var sb = window.__dcSupabase;
    var tries = 0;
    (function go() {
      sb = window.__dcSupabase;
      if (!sb || !sb.url || !sb.anonKey) {
        if (++tries < 40) setTimeout(go, 100);
        return;
      }
      fetch(sb.url + '/rest/v1/dc_templates?select=file_url,content_type&id=eq.' + encodeURIComponent(TPL), {
        headers: { apikey: sb.anonKey, Authorization: 'Bearer ' + sb.anonKey }
      })
        .then(function (r) { return r.json(); })
        .then(function (rows) {
          if (!Array.isArray(rows) || !rows.length || !rows[0].file_url) return null;
          if (!/svg/i.test(rows[0].content_type || rows[0].file_url)) return null;
          return fetch(rows[0].file_url).then(function (r) { return r.text(); });
        })
        .then(function (txt) { if (txt && !ROT) learn(txt); })
        .catch(function () {});
    })();
  })();

  /* ---- merge (same rules as sb-dc-tplmerge.js v3) ----------------------- */
  function n2(v) { return Math.round((+v || 0) * 100) / 100; }
  function cx(l) { return (+l.xIn || 0) + (+l.widthIn || 0) / 2; }
  function adv(l) { return (+l.fontSizeIn || 0) * (+l.lineHeight || 1); }

  function styleKey(l) {
    return [
      l.fontFamily || '', n2(l.fontSizeIn), l.fill || '', l.stroke || '',
      n2(l.strokeWidthIn), !!l.bold, !!l.italic, l.align || 'center',
      n2(l.lineHeight), n2(l.stretchX), n2(l.stretchY),
      !!l.underline, !!l.strikethrough, l.listStyle || 'none'
    ].join('|');
  }

  function mergeable(l) {
    return l && l.type === 'text' && !l.locked && l.visible !== false
      && !+l.rotation && !+l.curveDeg
      && String(l.text == null ? '' : l.text).length > 0;
  }

  function mergeLayers(layers) {
    var pool = layers.filter(mergeable);
    if (pool.length < 2) return layers;

    var buckets = {};
    pool.forEach(function (l) {
      var k = styleKey(l);
      (buckets[k] = buckets[k] || []).push(l);
    });

    var drop = {}, dropped = 0;
    Object.keys(buckets).forEach(function (k) {
      var rows = buckets[k].slice().sort(function (a, b) { return (+a.yIn) - (+b.yIn); });
      var used = {};
      rows.forEach(function (head) {
        if (used[head.id]) return;
        var chain = [head], last = head;
        for (;;) {
          var step = adv(last);
          if (!step) break;
          var want = (+last.yIn || 0) + step, tol = step * Y_TOL_FRAC, next = null;
          for (var i = 0; i < rows.length; i++) {
            var c = rows[i];
            if (used[c.id] || c === last || chain.indexOf(c) >= 0) continue;
            if (Math.abs((+c.yIn || 0) - want) > tol) continue;
            if (Math.abs(cx(c) - cx(last)) > CX_TOL) continue;
            next = c;
            break;
          }
          if (!next || chain.length >= MAX_LINES) break;
          chain.push(next);
          last = next;
        }
        if (chain.length < 2) return;
        chain.forEach(function (c) { used[c.id] = 1; });

        var first = chain[0], tail = chain[chain.length - 1];
        var width = 0, sum = 0;
        chain.forEach(function (c) {
          width = Math.max(width, +c.widthIn || 0);
          sum += cx(c);
        });
        var centre = sum / chain.length;
        var text = chain.map(function (c) { return c.text; }).join('\n');

        first.text = text;
        first.name = text.replace(/\n/g, ' ');
        first.heightIn = (+tail.yIn || 0) + (+tail.heightIn || 0) - (+first.yIn || 0);
        first.widthIn = width;
        first.xIn = centre - width / 2;
        for (var j = 1; j < chain.length; j++) { drop[chain[j].id] = 1; dropped++; info.removed++; }
        info.merged++;
      });
    });

    if (!dropped) return layers;
    return layers.filter(function (l) { return !drop[l.id]; });
  }

  /* ---- shrink (same rules as sb-dc-tplshrink.js v5) --------------------- */
  function shrinkLayers(layers) {
    layers.forEach(function (l) {
      if (!l || l.type !== 'text') return;
      if (+l.curveDeg || +l.rotation) return;          // tplrot / tplcurve own these
      var fs = +l.fontSizeIn || 0, box = +l.widthIn || 0;
      if (!fs || !box) return;
      var em = emWidth(l);
      if (!em) return;                                  // font not measurable yet
      var fits = box * MARGIN / em;
      if (fs - fits < fs * MIN_DROP) return;            // already fits
      var floor = fs * FLOOR;
      if (fits < floor) { fits = floor; info.floored++; }
      if (fs - fits < fs * MIN_DROP) return;
      l.fontSizeIn = fits;
      info.shrunk++;
    });
    return layers;
  }

  function pipeline(layers) {
    return shrinkLayers(mergeLayers(rotateLayers(layers)));
  }

  /* ---- entry point 1: the SVG converter --------------------------------- */
  function correct(built) {
    var t0 = (window.performance && performance.now()) || 0;
    var layers = (built && built.layers) || built;
    if (!Array.isArray(layers) || !layers.length) return built;

    var out = pipeline(layers.slice());

    info.builds++;
    info.ms.push(Math.round(((window.performance && performance.now()) || 0) - t0));
    if (built && built.layers) { built.layers = out; return built; }
    return out;
  }

  function wrap(fn) {
    if (!fn || fn.__prefit) return fn;
    var w = function (svgText) {
      try { learn(svgText); } catch (e) {}
      var r = fn.apply(this, arguments);
      /* the converter may be sync or async - tplboard already allows both */
      if (r && typeof r.then === 'function') {
        return r.then(function (v) {
          try { return correct(v); } catch (e) { info.why = 'err ' + e.message; return v; }
        });
      }
      try { return correct(r); }
      catch (e) { info.why = 'err ' + e.message; return r; }
    };
    w.__prefit = true;
    info.wrapped = true;
    return w;
  }

  /* ---- entry point 2: api.loadTemplate ---------------------------------- */
  function fixDoc(api) {
    var fr = api && api.front;
    if (window.SBDC914?.host.handled(fr)) return;
    if (!fr || !Array.isArray(fr.layers) || !fr.layers.length) return;
    if (typeof api.setDoc !== 'function') return;

    var m0 = info.merged, s0 = info.shrunk, r0 = info.rotated;
    var out = pipeline(fr.layers.map(function (l) {
      var c = {};
      for (var k in l) if (Object.prototype.hasOwnProperty.call(l, k)) c[k] = l[k];
      return c;
    }));
    if (info.merged === m0 && info.shrunk === s0 && info.rotated === r0) return;

    var doc = {};
    for (var k in fr) if (Object.prototype.hasOwnProperty.call(fr, k)) doc[k] = fr[k];
    doc.layers = out;
    api.setDoc(doc);
    info.docFixes++;
  }

  (function watchLoad() {
    setInterval(function () {
      var api = window.__dcApi;
      if (!api || typeof api.loadTemplate !== 'function' || api.loadTemplate.__prefit) return;
      var orig = api.loadTemplate;
      var w = function () {
        var r = orig.apply(this, arguments);
        info.loads++;
        if (Array.isArray(arguments[0]) && arguments[0].some(function (l) { return l && l.s914Document; })) return r;
        if (r && typeof r.then === 'function') {
          return r.then(function (v) {
            try { setTimeout(function () { fixDoc(window.__dcApi); }, 0); } catch (e) {}
            return v;
          });
        }
        try { setTimeout(function () { fixDoc(window.__dcApi); }, 0); } catch (e) {}
        return r;
      };
      w.__prefit = true;
      try { api.loadTemplate = w; } catch (e) { info.why = 'loadTemplate wrap failed'; }
    }, 100);
  })();

  /* The bundle defines __dcSvgToLayers while it boots, so this may run before
     or after that. Install a property trap either way. */
  var real = window.__dcSvgToLayers;
  if (typeof real === 'function') {
    window.__dcSvgToLayers = wrap(real);
  } else {
    var held = null;
    try {
      Object.defineProperty(window, '__dcSvgToLayers', {
        configurable: true,
        get: function () { return held; },
        set: function (v) { held = wrap(v); }
      });
    } catch (e) {
      info.why = 'trap failed: ' + (e && e.message);
    }
  }
})();
